/**
 * Mobile Detection Hook
 * Provides reactive mobile/tablet/desktop detection with manual override
 * 
 * Features:
 * - Auto-detect at < 768px (ON by default)
 * - Manual override toggle persisted in localStorage
 * - Manual override wins over auto-detect
 */
import { useState, useEffect, useCallback } from 'react';

// Breakpoints matching Tailwind defaults
const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
};

const MOBILE_MODE_KEY = 'rhino_mobile_mode_override';

export function useMobileDetection() {
  const [screenWidth, setScreenWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  
  // Manual override: null = auto, true = force mobile, false = force desktop
  const [manualOverride, setManualOverride] = useState(() => {
    if (typeof window === 'undefined') return null;
    const stored = localStorage.getItem(MOBILE_MODE_KEY);
    if (stored === 'true') return true;
    if (stored === 'false') return false;
    return null; // auto mode
  });

  // Computed values based on screen width
  const autoIsMobile = screenWidth < BREAKPOINTS.md;
  const autoIsTablet = screenWidth >= BREAKPOINTS.md && screenWidth < BREAKPOINTS.lg;
  const autoIsDesktop = screenWidth >= BREAKPOINTS.lg;

  // Final values: manual override wins if set
  const isMobile = manualOverride !== null ? manualOverride : autoIsMobile;
  const isTablet = manualOverride !== null ? false : autoIsTablet;
  const isDesktop = manualOverride !== null ? !manualOverride : autoIsDesktop;

  // Whether mobile nav should be shown (mobile mode active)
  const showMobileNav = isMobile;

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    // Initial check
    handleResize();

    // Listen for resize
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Toggle mobile mode manually
  const toggleMobileMode = useCallback(() => {
    setManualOverride(prev => {
      // We need to get the current auto state from window.innerWidth
      // because using autoIsMobile directly would cause stale closure issues
      const currentAutoIsMobile = window.innerWidth < BREAKPOINTS.md;
      
      let newValue;
      if (prev === null) {
        // Currently auto - if auto is mobile, switch to desktop; if auto is desktop, switch to mobile
        newValue = !currentAutoIsMobile;
      } else if (prev === true) {
        // Currently forced mobile - switch to auto
        newValue = null;
      } else {
        // Currently forced desktop - switch to mobile
        newValue = true;
      }
      
      // Persist to localStorage
      if (newValue === null) {
        localStorage.removeItem(MOBILE_MODE_KEY);
      } else {
        localStorage.setItem(MOBILE_MODE_KEY, String(newValue));
      }
      
      return newValue;
    });
  }, []);

  // Set mobile mode explicitly
  const setMobileMode = useCallback((enabled) => {
    if (enabled === null) {
      localStorage.removeItem(MOBILE_MODE_KEY);
    } else {
      localStorage.setItem(MOBILE_MODE_KEY, String(enabled));
    }
    setManualOverride(enabled);
  }, []);

  // Check if manual override is active
  const isManualOverride = manualOverride !== null;

  return {
    isMobile,
    isTablet,
    isDesktop,
    screenWidth,
    breakpoints: BREAKPOINTS,
    // Mobile nav specific
    showMobileNav,
    isManualOverride,
    manualOverride,
    toggleMobileMode,
    setMobileMode,
    // Raw auto-detected values (useful for UI)
    autoIsMobile,
    autoIsTablet,
    autoIsDesktop,
  };
}

export default useMobileDetection;
